import { whenHover } from '@blocksuite/affine-components/hover';
import { Peekable } from '@blocksuite/affine-components/peek';
import type { ReferenceInfo } from '@blocksuite/affine-model';
import {
  DEFAULT_DOC_NAME,
  REFERENCE_NODE,
} from '@blocksuite/affine-shared/consts';
import {
  DocDisplayMetaProvider,
  ToolbarRegistryIdentifier,
} from '@blocksuite/affine-shared/services';
import { affineTextStyles } from '@blocksuite/affine-shared/styles';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import {
  cloneReferenceInfo,
  referenceToNode,
} from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { LinkedPageIcon } from '@blocksuite/icons/lit';
import type { BlockComponent, BlockStdScope } from '@blocksuite/std';
import { BLOCK_ID_ATTR, ShadowlessElement } from '@blocksuite/std';
import {
  INLINE_ROOT_ATTR,
  type InlineRootElement,
  ZERO_WIDTH_FOR_EMBED_NODE,
  ZERO_WIDTH_FOR_EMPTY_LINE,
} from '@blocksuite/std/inline';
import type { DeltaInsert, DocMeta, Store } from '@blocksuite/store';
import { css, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { ReferenceNodeConfigProvider } from './reference-config';
import { RefNodeSlotsProvider } from './reference-node-slots';
import type { DocLinkClickedEvent } from './types';

// ── pipe: Core CRM entity references ──────────────────────────────────────────
// A doc references a Core entity by id (Phase-2 DoD: "a doc links to a CRM entity and the link
// resolves via the Core API"). The link is stored ID-ONLY in the Y.Doc (non-negotiable #2: never
// duplicate the entity's data) by encoding the entity id in the reference's `pageId` with a sentinel
// prefix — `pageId` is a free string in ReferenceInfoSchema, so this needs no schema fork. The label
// is resolved LIVE from the Core API at render time (cached); a deleted / forbidden entity renders an
// "unavailable" chip. (The same-origin fetch lives here in our owned fork for isolation; a DI'd
// provider is a Phase-6 refactor.) Keep this prefix in sync with the @-menu inserter (at-menu-config).
export const PIPE_ENTITY_REF_PREFIX = 'pipe-entity:';

type PipeEntityMeta = { title: string | null; kind: string };

const _pipeEntityCache = new Map<string, Promise<PipeEntityMeta | null>>();

/** Resolve a Core entity's display meta via the session-authed Core API; `null` = unavailable. */
function resolvePipeEntity(entityId: string): Promise<PipeEntityMeta | null> {
  const cached = _pipeEntityCache.get(entityId);
  if (cached) return cached;
  const pending = (async (): Promise<PipeEntityMeta | null> => {
    try {
      const res = await fetch(`/api/entities/${encodeURIComponent(entityId)}`, {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      // 404/401/403 are definitive "unavailable" (cache them); other non-2xx are transient (retry).
      if (res.status === 404 || res.status === 401 || res.status === 403) {
        return null;
      }
      if (!res.ok) {
        _pipeEntityCache.delete(entityId);
        return null;
      }
      const entity = (await res.json()) as { title?: string | null; type: string };
      return { title: entity.title ?? null, kind: entity.type };
    } catch {
      _pipeEntityCache.delete(entityId); // transient (offline) — allow a later retry
      return null;
    }
  })();
  _pipeEntityCache.set(entityId, pending);
  return pending;
}

@Peekable({ action: false })
export class AffineReference extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .affine-reference {
      white-space: normal;
      word-break: break-word;
      color: var(--affine-text-primary-color);
      fill: var(--affine-icon-color);
      border-radius: 4px;
      text-decoration: none;
      cursor: pointer;
      user-select: none;
      padding: 1px 2px 1px 0;

      svg {
        margin-bottom: 0.1em;
      }
    }
    .affine-reference:hover {
      background: var(--affine-hover-color);
    }

    .affine-reference[data-selected='true'] {
      background: var(--affine-hover-color);
    }

    .affine-reference-title {
      margin-left: 4px;
      border-bottom: 0.5px solid var(--affine-divider-color);
      transition: border 0.2s ease-out;
    }
    .affine-reference-title:hover {
      border-bottom: 0.5px solid var(--affine-icon-color);
    }
  `;

  get docTitle() {
    return this.refMeta?.title ?? DEFAULT_DOC_NAME;
  }

  private readonly _updateRefMeta = (doc: Store) => {
    // pipe entity references resolve via the Core API, not the workspace doc store.
    if (this.pipeEntityId) {
      return;
    }
    const refAttribute = this.delta.attributes?.reference;
    if (!refAttribute) {
      return;
    }

    const refMeta = doc.workspace.meta.docMetas.find(
      doc => doc.id === refAttribute.pageId
    );
    this.refMeta = refMeta
      ? {
          ...refMeta,
        }
      : undefined;
  };

  // Since the linked doc may be deleted, the `_refMeta` could be undefined.
  @state()
  accessor refMeta: DocMeta | undefined = undefined;

  // pipe: resolved Core entity meta for an entity reference — undefined = loading, null = unavailable.
  @state()
  accessor pipeEntity: PipeEntityMeta | null | undefined = undefined;

  /** The Core entity id this reference points at, or null for an ordinary doc reference. */
  get pipeEntityId(): string | null {
    const pageId = this.delta.attributes?.reference?.pageId;
    return pageId && pageId.startsWith(PIPE_ENTITY_REF_PREFIX)
      ? pageId.slice(PIPE_ENTITY_REF_PREFIX.length)
      : null;
  }

  get _icon() {
    const { pageId, params, title } = this.referenceInfo;
    return this.std
      .get(DocDisplayMetaProvider)
      .icon(pageId, { params, title, referenced: true }).value;
  }

  get _title() {
    const { pageId, params, title } = this.referenceInfo;
    return (
      this.std
        .get(DocDisplayMetaProvider)
        .title(pageId, { params, title, referenced: true }).value || title
    );
  }

  get block() {
    if (!this.inlineEditor?.rootElement) return null;
    const block = this.inlineEditor.rootElement.closest<BlockComponent>(
      `[${BLOCK_ID_ATTR}]`
    );
    return block;
  }

  get customContent() {
    return this.config.customContent;
  }

  get doc() {
    const doc = this.config.doc;
    return doc;
  }

  get inlineEditor() {
    const inlineRoot = this.closest<InlineRootElement<AffineTextAttributes>>(
      `[${INLINE_ROOT_ATTR}]`
    );
    return inlineRoot?.inlineEditor;
  }

  get referenceInfo(): ReferenceInfo {
    const reference = this.delta.attributes?.reference;
    const id = this.doc?.id ?? '';
    if (!reference) return { pageId: id };
    return cloneReferenceInfo(reference);
  }

  get selfInlineRange() {
    const selfInlineRange = this.inlineEditor?.getInlineRangeFromElement(this);
    return selfInlineRange;
  }

  readonly open = (event?: Partial<DocLinkClickedEvent>) => {
    if (!this.config.interactable) return;
    if (event?.event?.button === 2) {
      return;
    }

    this.std.getOptional(RefNodeSlotsProvider)?.docLinkClicked.next({
      ...this.referenceInfo,
      ...event,
      openMode:
        event?.event?.button === 1 ? 'open-in-new-tab' : event?.openMode,
      host: this.std.host,
    });
  };

  _whenHover = whenHover(
    hovered => {
      if (!this.config.interactable) return;

      const message$ = this.std.get(ToolbarRegistryIdentifier).message$;

      if (hovered) {
        message$.value = {
          flavour: 'affine:reference',
          element: this,
          setFloating: this._whenHover.setFloating,
        };
        return;
      }

      // Clears previous bindings
      message$.value = null;
      this._whenHover.setFloating();
    },
    { enterDelay: 500 }
  );

  override connectedCallback() {
    super.connectedCallback();

    this._whenHover.setReference(this);

    const message$ = this.std.get(ToolbarRegistryIdentifier).message$;

    this._disposables.add(() => {
      if (message$?.value) {
        message$.value = null;
      }
      this._whenHover.dispose();
    });

    if (!this.config) {
      console.error('`reference-node` need `ReferenceNodeConfig`.');
      return;
    }

    if (this.delta.insert !== REFERENCE_NODE) {
      console.error(
        `Reference node must be initialized with '${REFERENCE_NODE}', but got '${this.delta.insert}'`
      );
    }

    // pipe: resolve a Core entity reference's label live (cached); null => unavailable chip.
    const pipeEntityId = this.pipeEntityId;
    if (pipeEntityId) {
      resolvePipeEntity(pipeEntityId)
        .then(meta => {
          this.pipeEntity = meta;
        })
        .catch(() => {
          this.pipeEntity = null;
        });
    }

    const doc = this.doc;
    if (doc) {
      this._disposables.add(
        doc.workspace.slots.docListUpdated.subscribe(() =>
          this._updateRefMeta(doc)
        )
      );
    }

    this.updateComplete
      .then(() => {
        if (!this.inlineEditor || !doc) return;

        // observe yText update
        this.disposables.add(
          this.inlineEditor.slots.textChange.subscribe(() =>
            this._updateRefMeta(doc)
          )
        );
      })
      .catch(console.error);
  }

  // reference to block/element
  referenceToNode() {
    return referenceToNode(this.referenceInfo);
  }

  override render() {
    const refMeta = this.refMeta;
    const isDeleted = !refMeta;

    const attributes = this.delta.attributes;
    const reference = attributes?.reference;
    const type = reference?.type;
    if (!attributes || !type) {
      return nothing;
    }

    // pipe: a Core entity reference resolves its label via the Core API, not the doc store.
    if (this.pipeEntityId) {
      return this._renderPipeEntity(attributes);
    }

    const title = this._title;
    const icon = choose(type, [
      ['LinkedPage', () => this._icon],
      [
        'Subpage',
        () =>
          LinkedPageIcon({
            width: '1.25em',
            height: '1.25em',
            style:
              'user-select:none;flex-shrink:0;vertical-align:middle;font-size:inherit;margin-bottom:0.1em;',
          }),
      ],
    ]);

    const style = affineTextStyles(
      attributes,
      isDeleted
        ? {
            color: 'var(--affine-text-disable-color)',
            textDecoration: 'line-through',
            fill: 'var(--affine-text-disable-color)',
          }
        : {}
    );

    const content = this.customContent
      ? this.customContent(this)
      : html`${icon}<span
            data-title=${ifDefined(title)}
            class="affine-reference-title"
            >${title}</span
          >`;

    // we need to add `<v-text .str=${ZERO_WIDTH_FOR_EMBED_NODE}></v-text>` in an
    // embed element to make sure inline range calculation is correct
    return html`<span
      data-selected=${this.selected}
      class="affine-reference"
      style=${styleMap(style)}
      @click=${(event: MouseEvent) => this.open({ event })}
      @auxclick=${(event: MouseEvent) => this.open({ event })}
      >${content}<v-text .str=${ZERO_WIDTH_FOR_EMBED_NODE}></v-text
    ></span>`;
  }

  /** Render a Core entity reference: live label from the Core API, or an "unavailable" chip. */
  private _renderPipeEntity(attributes: AffineTextAttributes) {
    const meta = this.pipeEntity;
    const unavailable = meta === null;
    const label =
      meta === undefined
        ? DEFAULT_DOC_NAME
        : meta === null
          ? 'Unavailable entity'
          : meta.title || DEFAULT_DOC_NAME;
    const style = affineTextStyles(
      attributes,
      unavailable
        ? {
            color: 'var(--affine-text-disable-color)',
            textDecoration: 'line-through',
            fill: 'var(--affine-text-disable-color)',
          }
        : {}
    );
    const icon = LinkedPageIcon({
      width: '1.25em',
      height: '1.25em',
      style:
        'user-select:none;flex-shrink:0;vertical-align:middle;font-size:inherit;margin-bottom:0.1em;',
    });
    return html`<span
      data-selected=${this.selected}
      data-pipe-entity=${ifDefined(this.pipeEntityId ?? undefined)}
      class="affine-reference"
      style=${styleMap(style)}
      >${icon}<span data-title=${ifDefined(label)} class="affine-reference-title"
        >${label}</span
      ><v-text .str=${ZERO_WIDTH_FOR_EMBED_NODE}></v-text
    ></span>`;
  }

  override willUpdate(_changedProperties: Map<PropertyKey, unknown>) {
    super.willUpdate(_changedProperties);

    const doc = this.doc;
    if (doc) {
      this._updateRefMeta(doc);
    }
  }

  @property({ attribute: false })
  accessor config!: ReferenceNodeConfigProvider;

  @property({ type: Object })
  accessor delta: DeltaInsert<AffineTextAttributes> = {
    insert: ZERO_WIDTH_FOR_EMPTY_LINE,
    attributes: {},
  };

  @property({ type: Boolean })
  accessor selected = false;

  @property({ attribute: false })
  accessor std!: BlockStdScope;
}
